import { problemResponse, registry, undeclaredQueryRefused } from "../registry.js";
import { z } from "../zod.js";
import {
  AuthCompleted,
  ForgotPasswordRequest,
  LoginRequest,
  LoginResponse,
  RecoveryCodesResponse,
  RecoveryVerifyRequest,
  RecoveryVerifyResponse,
  RegenerateRecoveryCodesRequest,
  ResetPasswordRequest,
  ResetPasswordResponse,
  SessionListResponse,
  TotpActivateRequest,
  TotpEnrollRequest,
  TotpEnrollResponse,
  TotpVerifyRequest,
} from "./schemas.js";

// /v1/auth/* — the Idempotency-Key requirement is EXEMPT on this whole group
// (spec 16 §5: inherently idempotent-safe by design; the middleware does not
// reject its absence here, to keep browser flows simple). Server handlers
// carry the @IdempotencyExempt() route flag.

registry.register("LoginRequest", LoginRequest);
registry.register("LoginResponse", LoginResponse);
registry.register("AuthCompleted", AuthCompleted);
registry.register("ResetPasswordResponse", ResetPasswordResponse);
registry.register("SessionListResponse", SessionListResponse);
registry.register("TotpEnrollResponse", TotpEnrollResponse);
registry.register("RecoveryCodesResponse", RecoveryCodesResponse);
registry.register("RecoveryVerifyResponse", RecoveryVerifyResponse);

const sessionSecurity = [{ sessionCookie: [] }];

// What the session guard chain answers 403 on these routes. While an account
// must change its password, only password change, logout and the two
// second-factor challenges respond (spec 16 §2.5, D-339 ruling 48), and a
// read-only impersonation session may not mutate anything but its own logout
// (spec 16 §3.1.4a).
export const passwordChangeRequired = problemResponse(
  "PASSWORD_CHANGE_REQUIRED: the account must change its password first",
);
export const sessionMutationForbidden = problemResponse(
  "PASSWORD_CHANGE_REQUIRED, or IMPERSONATION_READ_ONLY for a read-only impersonation session",
);
// The second-factor challenges answer while a password change is pending, so
// the only 403 left on them is the impersonation fence.
const challengeMutationForbidden = problemResponse(
  "IMPERSONATION_READ_ONLY for a read-only impersonation session",
);

registry.registerPath({
  method: "post",
  path: "/v1/auth/login",
  operationId: "login",
  tags: ["identity"],
  summary: "Password login (first credential step)",
  description:
    "Generic 401 INVALID_CREDENTIALS for unknown email / wrong password / non-loginable accounts (no oracle). 423 ACCOUNT_LOCKED carries Retry-After and a locked_until extension member; 429 TOO_MANY_ATTEMPTS carries Retry-After (spec 16 §3.2–§3.3). On success sets the __Secure-assessly_sid cookie; step='totp' means the session is pending and only the TOTP allowlist responds.",
  request: { body: { content: { "application/json": { schema: LoginRequest } } } },
  responses: {
    200: {
      description: "Password accepted (session cookie set; may be pending TOTP)",
      content: { "application/json": { schema: LoginResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("INVALID_CREDENTIALS (generic — no account oracle)"),
    423: problemResponse("ACCOUNT_LOCKED — locked_until extension member + Retry-After header"),
    429: problemResponse("TOO_MANY_ATTEMPTS — Retry-After header"),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/auth/totp/verify",
  operationId: "verifyTotp",
  tags: ["identity"],
  summary: "Complete the TOTP challenge on a pending session",
  description:
    "±1 timestep tolerance; single-use timestep (replay of an accepted step is rejected). 5 consecutive failures revoke the pending session (spec 16 §3.2.8).",
  security: sessionSecurity,
  request: { body: { content: { "application/json": { schema: TotpVerifyRequest } } } },
  responses: {
    200: {
      description: "Challenge passed — session fully established",
      content: { "application/json": { schema: AuthCompleted } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("INVALID_TOTP_CODE, or NOT_AUTHENTICATED when no pending session"),
    403: challengeMutationForbidden,
    409: problemResponse("TOTP_NOT_PENDING — session is not awaiting a challenge"),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/auth/recovery/verify",
  operationId: "verifyRecoveryCode",
  tags: ["identity"],
  summary: "Recovery-code login (TOTP fallback) on a pending session",
  description:
    "Marks the matching unused code as used (single-use, race-safe). regeneration_recommended=true at ≤3 remaining; the running-low SES notice fires at ≤2 (spec 16 §3.2.8).",
  security: sessionSecurity,
  request: { body: { content: { "application/json": { schema: RecoveryVerifyRequest } } } },
  responses: {
    200: {
      description: "Code accepted — session fully established",
      content: { "application/json": { schema: RecoveryVerifyResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Invalid or already-used code"),
    403: challengeMutationForbidden,
    409: problemResponse("TOTP_NOT_PENDING"),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/auth/logout",
  operationId: "logout",
  tags: ["identity"],
  summary: "End the current session",
  security: sessionSecurity,
  responses: {
    204: { description: "Session revoked; Set-Cookie clears the session cookie" },
    400: undeclaredQueryRefused,
    401: problemResponse("Not authenticated"),
  },
});

registry.registerPath({
  method: "get",
  path: "/v1/auth/sessions",
  operationId: "listOwnSessions",
  tags: ["identity"],
  summary: "Own device list (active sessions)",
  security: sessionSecurity,
  responses: {
    200: {
      description: "Active sessions of the calling user",
      content: { "application/json": { schema: SessionListResponse } },
    },
    400: undeclaredQueryRefused,
    401: problemResponse("Not authenticated"),
    403: passwordChangeRequired,
  },
});

registry.registerPath({
  method: "delete",
  path: "/v1/auth/sessions/{id}",
  operationId: "revokeOwnSession",
  tags: ["identity"],
  summary: "Revoke one of the caller's own sessions",
  description: "Revocation is instant: the revoked session's very next request is a 401.",
  security: sessionSecurity,
  request: { params: z.object({ id: z.guid() }) },
  responses: {
    204: { description: "Revoked" },
    400: problemResponse("Validation failed (the id is not a uuid)"),
    401: problemResponse("Not authenticated"),
    403: sessionMutationForbidden,
    404: problemResponse("Unknown, already revoked, or another user's session"),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/auth/password/forgot",
  operationId: "forgotPassword",
  tags: ["identity"],
  summary: "Request a password-reset email",
  description:
    "Always 202 (no user oracle) — including for unknown emails, throttled requests (3/h per email), and accounts whose email is unverified (spec 16 §3.4, §3.5a).",
  request: { body: { content: { "application/json": { schema: ForgotPasswordRequest } } } },
  responses: {
    202: { description: "Accepted (a mail is sent only when the account qualifies)" },
    400: problemResponse("Validation failed"),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/auth/password/reset",
  operationId: "resetPassword",
  tags: ["identity"],
  summary: "Consume a reset token and set a new password",
  description:
    "Single-use, 30-minute tokens; newest-token-wins (older ones are superseded). On success every other session is revoked (revoke_reason='password_changed') and the response carries a fresh session cookie; step='totp' when the account has TOTP enrolled — the fresh session is pending until the challenge passes (spec 16 §3.4.4).",
  request: { body: { content: { "application/json": { schema: ResetPasswordRequest } } } },
  responses: {
    200: {
      description: "Password set — fresh session cookie set (pending TOTP when enrolled)",
      content: { "application/json": { schema: ResetPasswordResponse } },
    },
    400: problemResponse("INVALID_OR_EXPIRED_TOKEN (single generic code) or PASSWORD_POLICY"),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/auth/totp/enroll",
  operationId: "enrollTotp",
  tags: ["identity"],
  summary: "Start TOTP enrollment (returns otpauth URI + QR)",
  description:
    "Creates/replaces the pending secret. Requires fresh password re-entry (spec 16 §3.6.1). Human users only.",
  security: sessionSecurity,
  request: { body: { content: { "application/json": { schema: TotpEnrollRequest } } } },
  responses: {
    200: {
      description: "Pending secret created",
      content: { "application/json": { schema: TotpEnrollResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated / wrong password"),
    403: sessionMutationForbidden,
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/auth/totp/activate",
  operationId: "activateTotp",
  tags: ["identity"],
  summary: "Confirm enrollment with a first code; returns 10 recovery codes once",
  security: sessionSecurity,
  request: { body: { content: { "application/json": { schema: TotpActivateRequest } } } },
  responses: {
    200: {
      description: "TOTP active — plaintext recovery codes, shown exactly once",
      content: { "application/json": { schema: RecoveryCodesResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Bad code"),
    403: sessionMutationForbidden,
    409: problemResponse("NO_PENDING_ENROLLMENT"),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/auth/recovery-codes/regenerate",
  operationId: "regenerateRecoveryCodes",
  tags: ["identity"],
  summary: "Replace recovery codes (new batch of 10, shown once)",
  description:
    "Requires password + a current TOTP code; unused codes of the previous batch are hard-deleted (spec 16 §3.6.3).",
  security: sessionSecurity,
  request: {
    body: { content: { "application/json": { schema: RegenerateRecoveryCodesRequest } } },
  },
  responses: {
    200: {
      description: "New batch",
      content: { "application/json": { schema: RecoveryCodesResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Wrong password or TOTP code"),
    403: sessionMutationForbidden,
    409: problemResponse("TOTP_NOT_ENABLED"),
  },
});
